<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { fetchJson, formatCurrency } from '../helpers/orderHelpers';
import { fetchMarketplaceJson as fetchMarketplace } from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;
const fetchMarketplaceJson = path => fetchMarketplace(accountId, path);

const period = ref('today');
const loading = ref(false);
const errorMsg = ref('');
const chatAgents = ref([]);
const salesAgents = ref([]);
const helpKey = ref(null);

const PERIODS = [
  { key: 'today', label: 'Bugün' },
  { key: 'yesterday', label: 'Dün' },
  { key: 'this_week', label: 'Bu Hafta' },
  { key: 'this_month', label: 'Bu Ay' },
  { key: 'last_30', label: 'Son 30 Gün' },
];

const HELP = {
  konusma: {
    title: '💬 Cevaplanan Konuşma',
    text: 'Seçili dönemde bu temsilcinin cevap yazdığı farklı müşteri konuşması sayısı. Yani kaç ayrı kişiyle ilgilendiğini gösterir. Bir müşteriyle 10 kez yazışsa da 1 konuşma sayılır — böylece "kaç kişiye dokundu" net görünür.',
  },
  siparis: {
    title: '📦 Oluşturulan Sipariş',
    text: 'Bu temsilcinin sohbet üzerinden oluşturduğu sipariş sayısı (Koza/Ticimax + ModaXLarge/Shopify). Kaynak: sipariş oluşturulurken sohbete bağlanan kayıtlar. Not: yalnızca panel üzerinden açılan siparişleri kapsar.',
  },
  ciro: {
    title: '💰 Getirdiği Ciro',
    text: 'Temsilcinin oluşturduğu siparişlerin toplam tutarı. Emeğin doğrudan paraya dönüşen kısmı budur.',
  },
  donusum: {
    title: '📈 Dönüşüm Oranı',
    text: 'Her 100 konuşmadan kaçının siparişe döndüğü. Temsilcinin "satışa çevirme" gücünü gösterir. Yüksek konuşma + yüksek dönüşüm = hem çok çalışan hem iyi kapatan temsilci demektir. Düşükse ürün/fiyat sorunu da olabilir, tek başına suçlanmamalı.',
  },
  mesai: {
    title: '🕐 Çalışma Saati',
    text: 'İlk oku (🕐): temsilcinin o gün ilk müşteri mesajını yazdığı saat → son mesajını yazdığı saat. Yani "kaçta başladı, kaçta bitirdi". İkinci değer (⏱ aktif): moladan arınmış GERÇEK çalışma süresi — mesajlar arasındaki 30 dakikadan uzun boşluklar (yemek, ara) düşülerek hesaplanır. Örnek: 12:00–17:00 görünen ama arada 2 saat molası olan biri "5 saat aralık ama 3 saat aktif" olur. Botta 7/24 açık olduğu için aktif süre yüksek çıkar.',
  },
};

const norm = s => (s || '').toLocaleLowerCase('tr-TR').trim();

// YEREL tarihi UTC'ye çevirmeden formatla — toISOString() TR (UTC+3) saatini
// bir gün geriye kaydırıyordu ("bugün" dünü çekiyordu, 2026-07-08 bug'ı).
const fmt = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const dateRange = key => {
  const t = new Date();
  const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  let start = startOfDay(t);
  const end = startOfDay(t);
  if (key === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (key === 'this_week') {
    const dow = (t.getDay() + 6) % 7; // pazartesi = 0
    start.setDate(start.getDate() - dow);
  } else if (key === 'this_month') {
    start = new Date(t.getFullYear(), t.getMonth(), 1);
  } else if (key === 'last_30') {
    start.setDate(start.getDate() - 29);
  }
  return { start: fmt(start), end: fmt(end) };
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  const { start, end } = dateRange(period.value);
  const [conv, sales] = await Promise.allSettled([
    fetchMarketplaceJson(`reports/team_conversations?start=${start}&end=${end}`),
    fetchJson(`/ticimax-api/rapor?start=${start}&end=${end}`),
  ]);
  chatAgents.value = conv.status === 'fulfilled' ? conv.value.agents || [] : [];
  salesAgents.value =
    sales.status === 'fulfilled' ? sales.value.agents || [] : [];
  if (conv.status !== 'fulfilled') errorMsg.value = 'Konuşma verisi yüklenemedi.';
  loading.value = false;
};

const rows = computed(() => {
  const salesByName = {};
  salesAgents.value.forEach(a => {
    salesByName[norm(a.agentName)] = a;
  });
  return chatAgents.value.map(c => {
    const s = salesByName[norm(c.name)] || {};
    const orders = s.orderCount || 0;
    const revenue = s.totalRevenue || 0;
    return {
      ...c,
      orders,
      revenue,
      conversion: c.conversations ? (orders / c.conversations) * 100 : 0,
    };
  });
});

// Seçili dönemin okunur tarih etiketi (başlıkta gösterilir — Sevgi hangi güne
// baktığını net görsün: "Bugün · 8 Temmuz Salı" gibi).
const periodLabel = computed(() => {
  const { start, end } = dateRange(period.value);
  const opt = { day: 'numeric', month: 'long', weekday: 'long' };
  const s = new Date(`${start}T00:00:00`).toLocaleDateString('tr-TR', opt);
  if (start === end) return s;
  const e = new Date(`${end}T00:00:00`).toLocaleDateString('tr-TR', opt);
  return `${s} — ${e}`;
});

const humans = computed(() => rows.value.filter(r => !r.is_bot));
const bots = computed(() => rows.value.filter(r => r.is_bot));

const teamTotal = computed(() => ({
  conversations: humans.value.reduce((s, r) => s + r.conversations, 0),
  orders: humans.value.reduce((s, r) => s + r.orders, 0),
  revenue: humans.value.reduce((s, r) => s + r.revenue, 0),
  worked: humans.value.reduce((s, r) => s + (r.worked_hours || 0), 0).toFixed(1),
}));

const initials = name =>
  (name || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toLocaleUpperCase('tr-TR');

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-5xl w-full mx-auto px-6 py-8">
      <!-- Başlık -->
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div class="text-xs font-semibold uppercase tracking-widest text-n-slate-10">
            Müşteri Hizmetleri
          </div>
          <h1 class="text-3xl font-semibold text-n-slate-12 mt-1">🏆 Ekip Karnesi</h1>
          <div class="text-sm font-semibold text-woot-600 dark:text-woot-400 mt-1">📅 {{ periodLabel }}</div>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="p in PERIODS"
            :key="p.key"
            class="rounded-full px-3.5 py-1.5 text-sm font-semibold border"
            :class="period === p.key ? 'bg-woot-600 border-woot-600 text-white' : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2'"
            @click="period = p.key; load()"
          >{{ p.label }}</button>
        </div>
      </div>

      <div v-if="errorMsg" class="rounded-lg bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-4 py-3 mb-4 text-sm">
        {{ errorMsg }}
      </div>
      <div v-if="loading" class="text-n-slate-11 py-10 text-center">Yükleniyor…</div>

      <template v-if="!loading">
        <!-- Ekip özeti -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 text-center">
            <div class="text-3xl font-bold text-n-slate-12 tabular-nums">{{ teamTotal.conversations.toLocaleString('tr-TR') }}</div>
            <div class="text-sm text-n-slate-10 mt-1">kişiye cevap verildi</div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 text-center">
            <div class="text-3xl font-bold text-n-slate-12 tabular-nums">{{ teamTotal.orders.toLocaleString('tr-TR') }}</div>
            <div class="text-sm text-n-slate-10 mt-1">sipariş oluşturuldu</div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 text-center">
            <div class="text-3xl font-bold text-n-slate-12 tabular-nums">{{ formatCurrency(teamTotal.revenue) }}</div>
            <div class="text-sm text-n-slate-10 mt-1">toplam ciro</div>
          </div>
          <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 text-center">
            <div class="text-3xl font-bold text-n-slate-12 tabular-nums">{{ teamTotal.worked }} <span class="text-lg">saat</span></div>
            <div class="text-sm text-n-slate-10 mt-1">toplam aktif çalışma</div>
          </div>
        </div>

        <!-- Temsilci kartları -->
        <div class="flex items-center gap-2 mb-3">
          <h2 class="text-xl font-semibold text-n-slate-12">Müşteri Temsilcileri</h2>
          <span class="text-sm text-n-slate-10">— en çok çalışan üstte</span>
        </div>
        <div v-if="!humans.length" class="rounded-xl border border-dashed border-n-weak p-8 text-center text-n-slate-10">
          Bu dönemde temsilci etkinliği bulunamadı.
        </div>
        <div class="flex flex-col gap-3 mb-10">
          <div
            v-for="(r, i) in humans"
            :key="r.user_id"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 flex items-center gap-5"
          >
            <div class="flex items-center gap-3 w-56 shrink-0">
              <span class="text-lg font-bold text-n-slate-10 w-6 text-center">{{ i + 1 }}</span>
              <span class="w-11 h-11 rounded-full bg-woot-100 dark:bg-woot-900 text-woot-700 dark:text-woot-200 flex items-center justify-center font-bold shrink-0">{{ initials(r.name) }}</span>
              <div class="min-w-0">
                <div class="font-semibold text-n-slate-12 text-lg truncate">{{ r.name }}</div>
                <button v-if="r.first_at" class="text-xs text-n-slate-10 hover:text-n-slate-12 text-left" @click="helpKey = 'mesai'">
                  🕐 {{ r.first_at }}–{{ r.last_at }} · ⏱ {{ r.worked_hours }}s aktif ?
                </button>
              </div>
            </div>
            <div class="flex-1 grid grid-cols-4 gap-3 text-center">
              <div>
                <div class="text-2xl font-bold text-n-slate-12 tabular-nums">{{ r.conversations.toLocaleString('tr-TR') }}</div>
                <button class="text-xs text-n-slate-10 hover:text-n-slate-12" @click="helpKey = 'konusma'">💬 konuşma ?</button>
              </div>
              <div>
                <div class="text-2xl font-bold text-n-slate-12 tabular-nums">{{ r.orders.toLocaleString('tr-TR') }}</div>
                <button class="text-xs text-n-slate-10 hover:text-n-slate-12" @click="helpKey = 'siparis'">📦 sipariş ?</button>
              </div>
              <div>
                <div class="text-2xl font-bold text-n-slate-12 tabular-nums">{{ formatCurrency(r.revenue) }}</div>
                <button class="text-xs text-n-slate-10 hover:text-n-slate-12" @click="helpKey = 'ciro'">💰 ciro ?</button>
              </div>
              <div>
                <div class="text-2xl font-bold tabular-nums" :class="r.conversion >= 5 ? 'text-green-700 dark:text-green-400' : 'text-n-slate-12'">%{{ r.conversion.toFixed(1) }}</div>
                <button class="text-xs text-n-slate-10 hover:text-n-slate-12" @click="helpKey = 'donusum'">📈 dönüşüm ?</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Otomatik asistan (bot) — kıyas için ayrı -->
        <template v-if="bots.length">
          <h2 class="text-xl font-semibold text-n-slate-12 mb-3">🤖 Otomatik Asistan</h2>
          <div
            v-for="r in bots"
            :key="r.user_id"
            class="rounded-2xl border border-dashed border-n-weak bg-n-solid-2 p-5 flex items-center gap-5"
          >
            <div class="flex items-center gap-3 w-52 shrink-0">
              <span class="w-11 h-11 rounded-full bg-n-solid-3 flex items-center justify-center text-xl shrink-0">🤖</span>
              <span class="font-semibold text-n-slate-11 text-lg truncate">{{ r.name }}</span>
            </div>
            <div class="flex-1 grid grid-cols-4 gap-3 text-center text-n-slate-11">
              <div><div class="text-2xl font-bold tabular-nums">{{ r.conversations.toLocaleString('tr-TR') }}</div><div class="text-xs">💬 konuşma</div></div>
              <div><div class="text-2xl font-bold tabular-nums">{{ r.orders.toLocaleString('tr-TR') }}</div><div class="text-xs">📦 sipariş</div></div>
              <div><div class="text-2xl font-bold tabular-nums">{{ formatCurrency(r.revenue) }}</div><div class="text-xs">💰 ciro</div></div>
              <div><div class="text-2xl font-bold tabular-nums">%{{ r.conversion.toFixed(1) }}</div><div class="text-xs">📈 dönüşüm</div></div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- "?" AÇIKLAMA PENCERESİ -->
    <div v-if="helpKey" class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" @click.self="helpKey = null">
      <div class="max-w-xl w-full rounded-2xl bg-n-solid-1 border border-n-weak p-6 shadow-xl">
        <div class="flex items-start justify-between gap-4 mb-3">
          <h3 class="text-2xl font-semibold text-n-slate-12">{{ HELP[helpKey].title }}</h3>
          <button class="rounded-full border border-n-weak px-3 py-1 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-3" @click="helpKey = null">Kapat ✕</button>
        </div>
        <p class="text-lg leading-relaxed text-n-slate-12">{{ HELP[helpKey].text }}</p>
      </div>
    </div>
  </div>
</template>
